import { Injectable } from '@angular/core';
// http
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
// environments
import { environment } from '../../../environments/environment';
// services
import { RouteService } from './route.service';
// models
import { Customer } from '../models/customer.model';
import {
  Address,
  CreateMembershipData,
  DeliveryPreEstimateRequest,
  DeliveryRequestData,
  DeliveryType,
  FulfillmentType,
  Membership,
  OrderData,
  OrderDetails,
  OrderStatus,
  OrderStatusUser,
  SubscriptionPhase,
  UpdatedOrderInfo
} from '../models/order.model';
import { GroupOrder, GroupOrderRequest, Mode, Order, ShopLineItem } from '../models/payment-item.model';
// RxJs
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Coupon, Currency, Discount, KyooPromo, Price, Restaurant } from '../models/restaurant.model';
import { CartStore } from '../stores/cart.store';
import { PaymentStore } from '../stores/payment.store';
import { ConvertItemsService } from './convert-items.service';
import { FormatDataService } from './format-data.service';
import { CustomerUpdate } from '../interfaces';
import { Tip } from '../enums/tip.enum';
import { dataToCartItems, dataToDiscount, dataToOrdersStatuses, transformOrder } from '../dto/order.dto';
import { DiscountService } from './discount.service';
import { parseDuration } from '../dto/restaurant.dto';
import { ActivatedRoute } from '@angular/router';
import { GiftCardActivityDTO, GiftCardOrderDTO, GiftCardReloadDTO } from '../models/gift-card.model';
import { UrlParam } from '../enums/group-param.enum';
import { DiscountType } from '../enums/util.enum';
import { Shift } from '../models/flavor.model';
import { SERVICE_PROVIDER } from '../enums/merchant.enum';

@Injectable()
export class OrderApiService {
  private _calculateError: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  private readonly httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    })
  };

  private tip = Tip;

  constructor(
    private http: HttpClient,
    private routeService: RouteService,
    private formatDataService: FormatDataService,
    private cartStore: CartStore,
    private paymentStore: PaymentStore,
    private discountService: DiscountService,
    private route: ActivatedRoute
  ) {
  }

  public get(url: string): Observable<any> {
    return this.http.get(environment.apiUrl + url, this.httpOptions);
  }
  public post(url: string, body: object): Observable<any> {
    return this.http.post(environment.apiUrl + url, body, this.httpOptions);
  }
  public put(url: string, body: object): Observable<any> {
    return this.http.put(environment.apiUrl + url, body, this.httpOptions);
  }
  public delete(url: string): Observable<any> {
    return this.http.delete(environment.apiUrl + url, this.httpOptions);
  }

  public get calculateError(): string {
    return this._calculateError.value;
  }

  public sendOrder(locationId: string, order: Order, merchantUrl?: string): Observable<Object> {
    let apiURL = `${environment.apiUrl}v2/${this.routeService.getBaseUrl()}`;
    if (merchantUrl) {
      apiURL = `${environment.apiUrl}v2/${merchantUrl}`;
    }
    return this.http.post(`${apiURL}/locations/${locationId}/order`, order, this.httpOptions);
  }

  public sendGiftCardOrder(locationId: string, merchantId: string, giftCardOrderDTO:GiftCardOrderDTO): Observable<any> {
    let apiURL = `${environment.apiUrl}`;
    return this.http.post(`${apiURL}merchants/${merchantId}/locations/${locationId}/order/giftcard`, giftCardOrderDTO, this.httpOptions)
  }

  public sendGiftCardOrderV2(locationId: string, merchantId: string, giftCardOrderDTO:GiftCardOrderDTO): Observable<any> {
    let apiURL = `${environment.apiUrl}`;
    return this.http.post(`${apiURL}v2/merchants/${merchantId}/locations/${locationId}/order/giftcard`, giftCardOrderDTO, this.httpOptions)
  }

  public updateGiftCardOrderV2(locationId: string, merchantId: string, giftCardOrderDTO:GiftCardOrderDTO): Observable<any> {
    let apiURL = `${environment.apiUrl}`;
    return this.http.put(`${apiURL}v2/merchants/${merchantId}/locations/${locationId}/order/giftcard`, giftCardOrderDTO, this.httpOptions)
  }

  public sendGiftCardLoad(locationId: string, merchantId: string, giftCardActivityDTO:GiftCardActivityDTO): Observable<any> {
    let apiURL = `${environment.apiUrl}`;
    return this.http.post(`${apiURL}merchants/${merchantId}/locations/${locationId}/gift-cards/activity`, giftCardActivityDTO, this.httpOptions)
  }

  public sendGiftCardReload(merchantId: string, giftCardReloadDTO:GiftCardReloadDTO): Observable<any> {
    let apiURL = `${environment.apiUrl}`;
    return this.http.post(`${apiURL}merchants/${merchantId}/gift-cards/${giftCardReloadDTO.id}/auto-reload`,giftCardReloadDTO, this.httpOptions)
  }

  public deleteGiftCardReload(merchantId: string, giftCardId: string): Observable<any> {
    let apiURL = `${environment.apiUrl}`;
    return this.http.delete(`${apiURL}merchants/${merchantId}/gift-cards/${giftCardId}/auto-reload`, this.httpOptions)
  }

  public updateOrder(locationId: string, orderId: string, order: Order): Observable<Object> {
    this.updateLoyaltyInOrder(order);
    const apiURL = `${environment.apiUrl}v2/${this.routeService.getBaseUrl()}`;
    return this.http.put(`${apiURL}/locations/${locationId}/order/${orderId}`, order, this.httpOptions);
  }

  public updateAndSubmitOrder(locationId: string, order: Order, orderId: string): Observable<Object> {
    this.updateLoyaltyInOrder(order);
    const apiURL = `${environment.apiUrl}v3/${this.routeService.getBaseUrl()}`;
    return this.http.post(`${apiURL}/locations/${locationId}/order/${orderId}`, order, this.httpOptions);
  }

  public submitCloverOrder(locationId: string, order: Order): Observable<any> {
    const apiURL = `${environment.apiUrl}v4/${this.routeService.getBaseUrl()}`;
    return this.http.post(`${apiURL}/locations/${locationId}/order`, order, this.httpOptions);
  }

  public calculateOrder(locationId: string, order: Order, merchantUrl?: string): Observable<Object> {
    let apiURL = `${environment.apiUrl}v4/${this.routeService.getBaseUrl()}`;
    if (merchantUrl) {
      apiURL = `${environment.apiUrl}v4/${merchantUrl}`;
    }
    if (this.paymentStore.canRedeemLoyalty) {
      order.proposed_rewards = [{reward_tier_id: this.paymentStore.selectedLoyaltyReward.id}];
    }
    return this.http.post(`${apiURL}/locations/${locationId}/order/calculate`, order, this.httpOptions).pipe(
      map((response: any) => {
        response?.error ? this._calculateError.next(response?.error) : this._calculateError.next(null);
        return response;
      })
    );
  }

  public submitGroupOrder(groupId: string, order: GroupOrder): Observable<Object> {
    return this.http.post(`${environment.apiUrl}groups/${groupId}/order`, order, this.httpOptions);
  }

  public updateGroupTab(groupId: string, tabId: string, order: GroupOrderRequest[]): Observable<Object> {
    return this.http.put(`${environment.apiUrl}groups/${groupId}/tabs/${tabId}`, order, this.httpOptions);
  }

  public getGroupTabById(tabId: string): Observable<Object> {
    return this.http.get(`${environment.apiUrl}groups/tabs/${tabId}`, this.httpOptions);
  }

  public closeGroupTab(groupId: string, tabId: string, tip_amount: number): Observable<Object> {
    return this.http.post(`${environment.apiUrl}groups/${groupId}/tabs/${tabId}`, {tip_amount}, this.httpOptions);
  }

  public submitTab(locationId: string, order: Order): Observable<Object> {
    const apiURL = `${environment.apiUrl}v3/${this.routeService.getBaseUrl()}`;
    return this.http.post(`${apiURL}/locations/${locationId}/order`, order, this.httpOptions);
  }

  public updateTab(locationId: string, orderId: string, tabId: string): Observable<Object> {
    const apiURL = `${environment.apiUrl}v2/${this.routeService.getBaseUrl()}`;
    return this.http.put(`${apiURL}/locations/${locationId}/order/${orderId}/tab/${tabId}`, this.httpOptions);
  }

  public getTabById(tabId: string): Observable<Object> {
    return this.http.get(`${environment.apiUrl}v2/tabs/${tabId}`, this.httpOptions);
  }

  public closeTab(locationId: string, tabId: string, tip_amount: number): Observable<Object> {
    const tip = {tip_amount: tip_amount};
    const apiURL = `${environment.apiUrl}v2/${this.routeService.getBaseUrl()}`;
    return this.http.post(`${apiURL}/locations/${locationId}/tab/${tabId}`, tip, this.httpOptions);
  }

  public saveCustomer(customerId: string, customer: CustomerUpdate): Observable<Object> {
    return this.http.post(`${environment.apiUrl}customers/${customerId}`, customer, this.httpOptions);
  }

  public saveCustomerAddress(customerId: string, address: Address): Observable<Object> {
    return this.http.post(`${environment.apiUrl}customers/${customerId}/addresses`, address, this.httpOptions);
  }

  public getCustomerAddresses(customerId: string): Observable<Object> {
    return this.http.get(`${environment.apiUrl}customers/${customerId}/addresses`, this.httpOptions);
  }

  public getDeliveryPreEstimate(locationId: string, body: DeliveryPreEstimateRequest): Observable<Object> {
    return this.http.post(`${environment.apiUrl}deliveries/locations/${locationId}/pre-estimate`, body, this.httpOptions);
  }

  public getGroupDeliveryPreEstimate(groupId: string, body: DeliveryPreEstimateRequest): Observable<Object> {
    return this.http.post(`${environment.apiUrl}deliveries/groups/${groupId}/pre-estimate`, body, this.httpOptions);
  }

  public getDeliveryEstimate(locationId: string, body: DeliveryRequestData): Observable<Object> {
    return this.http.post(`${environment.apiUrl}deliveries/locations/${locationId}/estimate`, body, this.httpOptions);
  }

  public getGroupDeliveryEstimate(groupId: string, body: DeliveryRequestData): Observable<Object> {
    return this.http.post(`${environment.apiUrl}deliveries/groups/${groupId}/estimate`, body, this.httpOptions);
  }

  public cancelDelivery(transactionId: string): Observable<Object> {
    return this.http.get(`${environment.apiUrl}deliveries/transactions/${transactionId}/cancel`);
  }

  public getDiscountByPromoCode(locationId: string, code: string): Observable<Coupon> {
    const apiURL = environment.apiUrl + this.routeService.getBaseUrl();
    return this.http.get(`${apiURL}/locations/${locationId}/discounts?code=${code}`, this.httpOptions)
      .pipe(map(response => {
        if (response) {
          return dataToDiscount(response);
        }
      }));
  }

  public getSubscriptionData(locationId: string, subscriptionCode: string): Observable<SubscriptionPhase[]> {
    const apiURL = environment.apiUrl + this.routeService.getBaseUrl();
    const shortcodes = subscriptionCode === 'all' ? '' : `?shortcodes=${subscriptionCode}`;
    return this.http.get(`${apiURL}/locations/${locationId}/catalog/subscription_plans${shortcodes}`, this.httpOptions)
      .pipe(map((data: any[]) => {
        const subscriptions: SubscriptionPhase[] = [];
        const filteredSubscriptions: SubscriptionPhase[] = [];
        const sortedSubscriptions: SubscriptionPhase[] = [];

        for (const subscription of data) {
          const name = subscription.subscription_plan_data.name;
          const planId = subscription.id;

          // Navigate into each variation
          const variations = subscription.subscription_plan_data.subscription_plan_variations || [];
          for (const variation of variations) {
            const variationData = variation.subscription_plan_variation_data;
            if (!variationData || !variationData.phases) continue;

            for (const phase of variationData.phases) {
              const amount = phase.recurring_price_money?.amount ?? 0;
              const currency = phase.recurring_price_money?.currency ?? 'USD';

              subscriptions.push(new SubscriptionPhase(
                planId,
                name,
                phase.uid,
                phase.ordinal,
                phase.cadence,
                phase.periods,
                new Price(amount, currency),
                subscription.subscription_plan_data.eligible_item_ids,
                variation.id,
                phase.pricing
              ));
            }
          }
        }

        const includedPhases = [];
        for (const phase of subscriptions) {
          if (!includedPhases.includes(phase.cadence)) {
            includedPhases.push(phase.cadence);
          }
          filteredSubscriptions.push(phase);
        }

        // Add all MONTHLY subscriptions
        const monthlySubs = filteredSubscriptions.filter(s => s.cadence === 'MONTHLY');
        if (monthlySubs.length > 0) {
          sortedSubscriptions.push(...monthlySubs);
        }

        // Add all WEEKLY subscriptions
        const weeklySubs = filteredSubscriptions.filter(s => s.cadence === 'WEEKLY');
        if (weeklySubs.length > 0) {
          sortedSubscriptions.push(...weeklySubs);
        }

        // Add all DAILY subscriptions
        const dailySubs = filteredSubscriptions.filter(s => s.cadence === 'DAILY');
        if (dailySubs.length > 0) {
          sortedSubscriptions.push(...dailySubs);
        }

        return sortedSubscriptions.length > 0 ? sortedSubscriptions : filteredSubscriptions;
      }));
  }

  public createMembership(subscription: CreateMembershipData): Observable<any> {
    const body = {
      plan_id: subscription.plan_id,
      customer_id: subscription.customer_id,
      card_id: subscription.card_id,
      order_id: subscription.order_id,
      transaction_id: subscription.transaction_id,
      invoice_amount: subscription.invoice_amount,
      buyer_email: subscription.buyer_email,
      product_name: subscription.product_name
    };
    const apiURL = environment.apiUrl + this.routeService.getBaseUrl();
    return this.http.post(`${apiURL}/locations/${subscription.location_id}/subscriptions`, body, this.httpOptions);
  }

  public createGiftCardOrder(locationId: string, amount: number, sendDate:string, selectedPromo: KyooPromo): Order {
    const mode = localStorage.getItem('mode');
    let deliveryAddress: Address = null;
    if (this.paymentStore.deliveryAddress) {
      const {street, suite, city, state, postalCode, instructions} = this.paymentStore.deliveryAddress;
      deliveryAddress = new Address(street, suite, city, state, postalCode, instructions);
    }
    const orderDetails: OrderDetails = {
      tipMoney: 0,
      delivery_gratuity_cents: 0,
      fulfillmentType: FulfillmentType.GIFTCARD,
      familyName: this.paymentStore.familyName,
      givenName: this.paymentStore.givenName,
      tableNumber: this.paymentStore.tableNumber,
      isTabOrder: this.paymentStore.createTabChecked,
      isGroupOrder: this.paymentStore.isGroupOrder,
      pickupAt: sendDate,
      pickupLocation: this.cartStore.pickupLocation,
      deliveryAt: this.cartStore.deliveryAt,
      deliveryQuoteId: this.cartStore.deliveryResponseData ? this.cartStore.deliveryResponseData.quote_id : null,
      email: this.paymentStore.email,
      billingAddress: this.paymentStore.billingAddress,
    };
    const price = new Price(amount,Currency.USD)
    // Promo logic (if applicable)
    const promoPrice = selectedPromo?.promoPrice ?? amount;
    const spendPrice = selectedPromo?.spendPrice ?? amount;
    const discountAmount = promoPrice - spendPrice;

    const discounts = discountAmount > 0 ? [
      new Discount(
        null,
        null,
        null,
        DiscountType.FIXED_AMOUNT,
        "ORDER",
        null,
        new Price(discountAmount),
        null,
        null,
        null,
        "Gift Card Promo",
        null
      )
    ] : [];

    const giftCardLineItem: ShopLineItem = {
      amount,
      line_items: [{
        name: "GiftCard",
        quantity: 1,
        item_type: "GIFT_CARD",
        base_price_money: price
      }],
      discounts
    }
    const order = this.getOrder(this.paymentStore.customer, giftCardLineItem, orderDetails);
    order.platform_name = this.setPlatformName(mode,order);
    order.is_requesting_sms_link = mode === Mode.mobile;
    order.square_customer_id = this.paymentStore.customer.squareCustomerId;
    order.platform_user_id = this.paymentStore.customer.phone;
    order.order_bot_user_id = this.paymentStore.customer.orderBotUserId;
    return order;
  }

  public createOrder(locationId: string): Order {
    const shopLineItem = ConvertItemsService.fromCartItemsToShopLineItems(this.cartStore.items);
    this.discountService.applyDiscountToOrder(shopLineItem);
    const tipMoney = this.cartStore.getTip(this.tip.restaurant) * 100;
    const tipMoneyDriver = this.cartStore.getTip(this.tip.driver) * 100;
    const mode = localStorage.getItem('mode');
    let deliveryAddress: Address = null;
    if (this.paymentStore.deliveryAddress) {
      const {street, suite, city, state, postalCode, instructions} = this.paymentStore.deliveryAddress;
      deliveryAddress = new Address(street, suite, city, state, postalCode, instructions);
    }
    const orderDetails: OrderDetails = {
      tipMoney,
      delivery_gratuity_cents: tipMoneyDriver,
      fulfillmentType: this.cartStore.isSubscription ? null : this.paymentStore.fulfilmentType,
      familyName: this.paymentStore.familyName,
      givenName: this.paymentStore.givenName,
      tableNumber: this.paymentStore.tableNumber,
      isTabOrder: this.paymentStore.createTabChecked,
      isGroupOrder: this.paymentStore.isGroupOrder,
      pickupAt: this.cartStore.pickupAt,
      pickupLocation: this.cartStore.pickupLocation,
      deliveryAt: this.cartStore.deliveryAt,
      deliveryQuoteId: this.cartStore.deliveryResponseData ? this.cartStore.deliveryResponseData.quote_id : null,
      email: this.paymentStore.email,
      billingAddress: this.paymentStore.billingAddress,
      deliveryAddress: deliveryAddress,
      printAheadDuration: this.cartStore.printAheadDuration,
      pickupWindowDuration: this.cartStore.printAheadDuration,
      saveDeliveryAddress: this.paymentStore.saveDeliveryAddress,
      isAsap: this.cartStore.isAsap,
      useLoyaltyPoints: this.paymentStore.canRedeemLoyalty
    };
    const order = this.getOrder(this.paymentStore.customer, shopLineItem[locationId], orderDetails);
    if (this.route.snapshot.queryParams?.customInstruction) {
      order.custom_number = this.route.snapshot.queryParams?.customInstruction;
      if (this.paymentStore.fulfilmentType === 'tableside' || this.paymentStore.fulfilmentType === 'tab') {
        this.paymentStore.tableNumber = this.route.snapshot.queryParams?.customInstruction;
      }
    }
    const customOrderNumber = sessionStorage.getItem(UrlParam.customInstruction);
    if (customOrderNumber) {
      order.custom_number = customOrderNumber;
      if (this.paymentStore.fulfilmentType === 'tableside' || this.paymentStore.fulfilmentType === 'tab') {
        this.paymentStore.tableNumber = this.route.snapshot.queryParams?.customInstruction;
      }
    }
    order.platform_name = this.setPlatformName(mode,order);
    order.is_requesting_sms_link = mode === Mode.mobile;
    order.proposed_discount_codes = this.cartStore.getCoupons();
    order.square_customer_id = this.paymentStore.customer.squareCustomerId;
    order.platform_user_id = this.paymentStore.customer.phone;
    order.order_bot_user_id = this.paymentStore.customer.orderBotUserId;
    order.is_subscription_order = this.cartStore.isSubscription;
    return order;
  }

  private setPlatformName(mode: string, order: Order) {
    if(mode === Mode.mobile) {
      return Mode.mobile
    } else if(mode === Mode.mobileApp) {
      return Mode.mobileApp
    }
    return order.platform_name
  }

  public createGroupOrder(restaurants: Restaurant[], shifts:Shift[]=[]): GroupOrder {
    const groupOrderRequests = [];
    const mode = localStorage.getItem('mode');
    for (const restaurant of restaurants) {
      const orderMap = this.cartStore.orderMap[restaurant.locationId];
      const shopLineItem = ConvertItemsService.fromCartItemsToShopLineItems(this.cartStore.items);
      if (shifts?.length) {
        const shift = shifts.find(s => s.truck_id.kyoo_location_id == restaurant.locationId);
        if (shift) {
          shopLineItem[restaurant.locationId].metadata.vendor_name =shift.truck_id.name;
          shopLineItem[restaurant.locationId].metadata.location_name =shift.location_id.name;
        }
      }
      const entry = shopLineItem[restaurant.locationId];
      groupOrderRequests.push(new GroupOrderRequest(restaurant.locationId, orderMap.id, entry));
    }
    let deliveryAddress: Address = null;
    if (this.paymentStore.deliveryAddress) {
      const {street, suite, city, state, postalCode, instructions} = this.paymentStore.deliveryAddress;
      deliveryAddress = new Address(street, suite, city, state, postalCode, instructions);
    }
    const orderDetails: OrderDetails = {
      tipMoney: this.cartStore.getTip(this.tip.restaurant) * 100,
      delivery_gratuity_cents: this.cartStore.getTip(this.tip.driver) * 100,
      fulfillmentType: this.paymentStore.fulfilmentType,
      familyName: this.paymentStore.familyName,
      givenName: this.paymentStore.givenName,
      tableNumber: this.paymentStore.tableNumber,
      isTabOrder: this.paymentStore.createTabChecked,
      pickupAt: this.cartStore.pickupAt,
      deliveryAt: this.cartStore.deliveryAt,
      deliveryQuoteId: this.cartStore.deliveryResponseData ? this.cartStore.deliveryResponseData.quote_id : null,
      email: this.paymentStore.email,
      billingAddress: this.paymentStore.billingAddress,
      deliveryAddress: deliveryAddress,
      isAsap: this.cartStore.isAsap,
      createOrderRequest: groupOrderRequests,
      saveDeliveryAddress: this.paymentStore.saveDeliveryAddress,
      platformName: mode === Mode.mobile ? Mode.mobile : 'web'
    };

    return this.getGroupOrder(this.paymentStore.customer, orderDetails);
  }

  public calculateOrders(removeDiscount: boolean = false): void {
    this.paymentStore.isWaitingUpdate = true;
    const locationsIds = Object.keys(this.cartStore.orderMap);
    for (const locationId of locationsIds) {
      this.calculateSingleOrder(locationId, removeDiscount);
    }
  }

  public calculateSingleOrder(locationId: string, removeDiscount: boolean = false): void {
    const orderMap = this.cartStore.orderMap[locationId];
    const order = this.getOrderWithTipMoney(locationId);
    if (removeDiscount) {
      order.proposed_discount_codes = [];
    }
    order.ikon_pass_id = this.paymentStore.customer?.ikon_pass_id;
    this.calculateOrder(locationId, order)
      .subscribe((response: any) => {
        this.updateOrderData(
          locationId,
          order,
          orderMap,
          response.total_money,
          response.taxes,
          response.discounts,
          response. accumulated_loyalty_points,
          response.line_items,
          response.estimated_duration,
          response.kyoo_fee_money.amount,
          response.delivery_fee_money.amount,
          response.total_tax_money.amount
        );
      }, () => this.paymentStore.isWaitingUpdate = false);
  }

  public updateOrderData(
    locationId,
    order,
    orderMap,
    totalMoney,
    taxes,
    discounts,
    loyaltyPoints,
    lineItems,
    estimated_duration: string = null,
    kyoo_fee_money: number = null,
    delivery_fee_money: number = null,
    total_tax_money: number = null
  ): void  {
    const totalPrice = totalMoney.amount - this.cartStore.taxesSum(taxes);
    this.cartStore.updateOrderMapData(
      locationId,
      order,
      totalPrice,
      taxes,
      discounts,
      loyaltyPoints,
      lineItems,
      estimated_duration,
      kyoo_fee_money,
      delivery_fee_money,
      total_tax_money
    );
    if (order.is_tab_order) { this.updateTipsInTabOrder(); }
    this.paymentStore.orderData = new OrderData(orderMap.id, order, locationId);
    this.paymentStore.isWaitingUpdate = false;
  }

  public deleteItemFromOrderMap(i: number, merchantUrl?: string): void  {
    const {locationId, item} = this.cartStore.items[i];
    const orderMap = this.cartStore.orderMap[locationId];
    const isLastLocationItemInCart = orderMap.order.createOrderRequest.line_items.length === 1;
    this.cartStore.deleteItemFromCart(i);
    const order = this.createOrder(locationId);
    if (isLastLocationItemInCart) {
      this.removeLocationFromOrderMap(locationId);
    } else {
      order.ikon_pass_id = this.paymentStore.customer?.ikon_pass_id;
      this.calculateOrder(locationId, order, merchantUrl)
        .subscribe((response: any) => {
          this.cartStore.updateOrderMapData(
            locationId,
            order,
            response.total_money.amount,
            response.taxes,
            response.discounts,
            response.accumulated_loyalty_points,
            response.line_items,
            response.estimated_duration,
            response.kyoo_fee_money.amount,
            response.delivery_fee_money.amount,
            response.total_tax_money.amount
          );
          const orderLineItems = this.cartStore.orderMap[locationId].order.createOrderRequest.line_items;
          this.cartStore.orderMap[locationId].order.createOrderRequest.line_items = orderLineItems.filter(cartItem => cartItem.catalog_object_id !== item.id);
          if (order.is_tab_order) { this.updateTipsInTabOrder(); }
          this.cartStore.updateCartInProgress = false;
          this.paymentStore.isWaitingUpdate = false;
        });
    }
  }

  public removeLocationFromOrderMap(locationId: string): void  {
    const newOrderMap = {};
    const locationsIds = Object.keys(this.cartStore.orderMap);
    for (const id of locationsIds) {
      if (id !== locationId) {
        newOrderMap[id] = this.cartStore.orderMap[id];
      }
    }
    this.cartStore.orderMap = newOrderMap;
    this.cartStore.updateCartInProgress = false;
    this.paymentStore.isWaitingUpdate = false;
  }

  public removeNoItemsLocationsFromCheckout(): void  {
    const newOrderMap = {};
    const locationsIds = Object.keys(this.cartStore.orderMap);
    for (const id of locationsIds) {
      if (this.cartStore.orderMap[id].order.createOrderRequest) {
        newOrderMap[id] = this.cartStore.orderMap[id];
      }
    }
    this.cartStore.orderMap = newOrderMap;
    this.cartStore.updateCartInProgress = false;
  }

  public getOrder(customer: Customer, lineItems: ShopLineItem,
                  {
                    tipMoney, pickupAt, pickupLocation, printAheadDuration, pickupWindowDuration,
                    givenName, familyName, fulfillmentType, tableNumber, isTabOrder, isGroupOrder,
                    email, billingAddress, deliveryAt, deliveryQuoteId,
                    deliveryAddress, saveDeliveryAddress, isAsap, useLoyaltyPoints
                  }: OrderDetails): Order {
    const merchantId = this.routeService.getMerchantId();
    const {squareCustomerId, phone, orderBotUserId} = {...customer};
    return new Order(lineItems, squareCustomerId, phone, orderBotUserId, orderBotUserId, merchantId,
      tipMoney, pickupAt, pickupLocation, printAheadDuration, pickupWindowDuration, givenName, familyName, tableNumber,
      fulfillmentType, isTabOrder, isGroupOrder, email, billingAddress, deliveryAt, deliveryQuoteId, deliveryAddress,
      saveDeliveryAddress, isAsap, useLoyaltyPoints);
  }


  public getGroupOrder(customer: Customer,
                       {
                         tipMoney, pickupAt, givenName, familyName, fulfillmentType, tableNumber, isTabOrder,
                         email, billingAddress, isAsap, createOrderRequest, platformName, deliveryAt, deliveryQuoteId,
                         deliveryAddress, saveDeliveryAddress
                  }: OrderDetails): GroupOrder {
    const {squareCustomerId, phone, orderBotUserId} = {...customer};
    return new GroupOrder(platformName, phone, orderBotUserId, orderBotUserId, squareCustomerId, createOrderRequest, fulfillmentType, isAsap,
      pickupAt, tableNumber, isTabOrder, tipMoney, givenName, familyName, billingAddress, email, deliveryAt, deliveryQuoteId,
      deliveryAddress, saveDeliveryAddress );
  }

  public getOrderWithTipMoney(locationId: string): Order {
    const orderMap = this.cartStore.orderMap[locationId];
    const orderPercent = this.cartStore.totalPrice > 0 ? (orderMap.amount / (this.cartStore.totalPrice * 100)) : 1;
    const order = this.createOrder(locationId);
    const orderTips = this.getTipForPayment(this.tip.restaurant, order.fulfillment_type, this.cartStore.enableRestaurantTips, orderPercent);
    const orderTipsDriver = this.getTipForPayment(this.tip.driver, order.fulfillment_type, this.cartStore.enableRestaurantTips, orderPercent);
    orderMap.order.tip_money = orderTips;
    orderMap.order.delivery_gratuity_cents = orderTipsDriver;
    order.tip_money = orderTips;
    order.delivery_gratuity_cents = orderTipsDriver;
    order.estimated_prep_time_duration = orderMap?.estimatedDurationDefault ? orderMap?.estimatedDurationDefault : null;
    order.ikon_pass_id = this.paymentStore.customer?.ikon_pass_id;
    return order;
  }

  private getTipForPayment(tipType: Tip, fulfillmentType: FulfillmentType, enableRestaurant: boolean, orderPercent: number): number {
    let isDelivery = false;
    if (fulfillmentType === FulfillmentType.DELIVERY) {
      isDelivery = true;
    }
    switch (tipType) {
      case Tip.driver: return this.getDriverForPayment(isDelivery, orderPercent);
      case Tip.restaurant: return this.getRestaurantForPayment(enableRestaurant, isDelivery, orderPercent);
    }
  }

  private getDriverForPayment(isDelivery: boolean, orderPercent: number): number {
    if (isDelivery && this.cartStore.deliveryType === DeliveryType.COURIER) {
      return Math.round(this.cartStore.getTip(this.tip.driver) * orderPercent * 100);
    }
    return 0;
  }

  private getRestaurantForPayment(enableRestaurant: boolean, isDelivery: boolean, orderPercent: number): number {
    if (!enableRestaurant && isDelivery && this.cartStore.deliveryType === DeliveryType.COURIER) {
      return 0;
    }
    return Math.round(this.cartStore.getTip(this.tip.restaurant) * orderPercent * 100);
  }

  public getAllOrdersList(locationId: string, isOnlyPendingValue: boolean, isGroup?: boolean): Observable<OrderStatus[]> {
    let baseUrl = `${environment.apiUrl}${this.routeService.getBaseUrl()}`;
    let url = `${baseUrl}/locations/${locationId}/orders`;
    if (isGroup) {
      baseUrl = `${environment.apiUrl}${this.routeService.getGroupUrl()}`;
      url = `${baseUrl}/orders`;
    }

    return this.http.get<OrderStatus[]>(url, {
      params: new HttpParams().set('isOnlyPending', String(isOnlyPendingValue))
    })
      .pipe(
        map(orders => {
          return orders.map(order => {
            return this.returnOrdersStatusArray(order, isGroup);
          });
        })
      );
  }

  public getUserOrder(locationId: string, merchantId: string, orderBotUserId: string): Observable<OrderStatusUser[]> {
    const url = `${environment.apiUrl}v2/merchants/${merchantId}/locations/${locationId}/customers/${orderBotUserId}/orders`;
    return this.http.get(url).pipe(
      map((response: any[]) => {
        return response.map(order => {
          return transformOrder(order);
        });
      }),
    );
  }

  public getGroupUserOrder(groupId: string, orderBotUserId: string): Observable<OrderStatusUser[]> {
    const url = `${environment.apiUrl}v2/groups/${groupId}/customers/${orderBotUserId}/orders`;
    return this.http.get(url).pipe(
      map((response: any[]) => {
        return response.map(order => {
          return transformOrder(order);
        });
      }),
    );
  }

  public updateOrderStatus(status: string, locationId: string, orderId: string): Observable<Object> {
    const url = `${environment.apiUrl}merchants/${this.routeService.getMerchantId()}/locations/${locationId}/orders/${orderId}`;
    const body = {state: status};
    return this.http.put<UpdatedOrderInfo>(url, JSON.stringify(body), this.httpOptions);
  }

  private updateTipsInTabOrder(): void {
    this.cartStore.setTip(+((this.cartStore.autoGratuityPercent / 100) * this.cartStore.totalPrice).toFixed(2));
  }

  private updateLoyaltyInOrder(order: Order): void {
    if (this.paymentStore.loyaltyEnabled) {
      order.square_loyalty_account_id = this.paymentStore.customer.loyaltyAccountId;
    }
    if (this.paymentStore.canRedeemLoyalty) {
      order.proposed_rewards = [{reward_tier_id: this.paymentStore.selectedLoyaltyReward.id}];
    }
  }

  private returnOrdersStatusArray(order: any, isGroup: boolean = false): OrderStatus {
    const cartItems = isGroup
      ? dataToCartItems(order.line_items)
      : dataToCartItems(order.square_order.line_items);

    return new OrderStatus(
      isGroup ? order.group_order_id : order.square_order.id,
      isGroup ? order.guest_check_numbers?.join(', #') : order.guest_check_number,
      isGroup ? order.status : order.square_order.fulfillments?.[0]?.state,
      isGroup ? order.customer_id : order.square_customer_id,
      isGroup ? order.created_at : order.datetime_created,
      order.fulfillment_type,
      order.is_asap,
      order.pickup_at,
      cartItems,
      order?.square_location_id,
      '',
      order?.square_receipt_url,
      parseDuration(order?.prep_time_duration),
    );
  }

 // public updateOrderMapPickupLocation(locationId: string): void {
 //     this.cartStore.orderMap[locationId].order.pickupLocation = this.cartStore.pickupLocation;
 // }

  public getPerviousOrders(orderBotUserId: string, page: number = 0, size: number = 5, merchantId?: string): Observable<OrderStatus[]> {
    let baseUrl =  environment.apiUrl + this.routeService.getBaseUrl();
    const url = `/customers/${orderBotUserId}/orders?page=${page}&size=${size}`;
    if (merchantId) {
      baseUrl = environment.apiUrl + `merchants/${merchantId}`
    }
    return this.http.get(baseUrl + url).pipe(
      map((order: any) => dataToOrdersStatuses(order))
    );
  }

  public getCustomerMemberships(customerId: string, locationId?: string, merchantId?: string): Observable<Membership[]> {
    let baseUrl;
    let url;
    if(!locationId && !merchantId) {
      baseUrl = `${environment.apiUrl}${this.routeService.getLocationUrl()}`;
      url = `${baseUrl}/customers/${customerId}/subscriptions`;
    } else {
      baseUrl = environment.apiUrl
      url = `${baseUrl}merchants/${merchantId}/locations/${locationId}/customers/${customerId}/subscriptions`
    }

    return this.http.get<any[]>(url)
      .pipe(
        map(response => response.map(membership => this.mapMembership(membership)))
      );
  }

  public cancelMembership(membershipId: string): Observable<any> {
    const baseUrl = `${environment.apiUrl}${this.routeService.getLocationUrl()}`;
    const url = `${baseUrl}/subscriptions/${membershipId}/cancel`;

    return this.http.post<any>(url, {}).pipe(
      map(membership => this.mapMembership(membership))
    );
  }

  public resumeMembership(membershipId: string): Observable<any> {
    const baseUrl = `${environment.apiUrl}${this.routeService.getLocationUrl()}`;
    const url = `${baseUrl}/subscriptions/${membershipId}/resume`;

    return this.http.post<any>(url, {}).pipe(
      map(membership => this.mapMembership(membership))
    );
  }

  public getMembership(membershipId: string): Observable<Membership> {
    const baseUrl = `${environment.apiUrl}${this.routeService.getLocationUrl()}`;
    const url = `${baseUrl}/subscriptions/${membershipId}`;

    return this.http.get<any>(url).pipe(
      map(membership => this.mapMembership(membership))
    );
  }

  private mapMembership(membership: any): Membership {
    return new Membership(
      membership.subscription_id,
      membership.plan_id,
      membership.customer_id,
      membership.status,
      membership.start_date,
      membership.canceled_date,
      membership.charged_through_date,
      membership.actions
    );
  }

  public unlockPreviousFridgeOrder(locationId: string, transactionId: string): Observable<string> {
    const baseUrl = environment.apiUrl;
    const url = `fridge/location/${locationId}/transaction/${transactionId}/lock`;

    return this.http.post(baseUrl + url, null, { responseType: 'text' });
  }

}
